import { isEqual } from 'lodash';
import { WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceView } from '../../../types/WorkspaceView';
import { ViewColumnUtils, WorkspaceViewColumn } from '../../../types/WorkspaceViewColumn';

export function unequalViewIndexes(
  framework: WorkspaceFramework,
  localViews: WorkspaceView[],
  remoteViews: WorkspaceView[]
): number[] {
  const unequalViewIndexes: number[] = [];
  for (let i = 0; i < localViews.length; i++) {
    const localView = localViews[i];
    const remoteView = remoteViews.find((view) => view.id === localView.id);
    if (!remoteView || !areViewsEqual(framework, localView, remoteView)) {
      unequalViewIndexes.push(localView.id);
    }
  }

  return unequalViewIndexes;
}

export function areViewsEqual(framework: WorkspaceFramework, view1?: WorkspaceView, view2?: WorkspaceView): boolean {
  if (!view1 || !view2) {
    return false;
  }
  const {
    default: default1,
    orderIndex: orderIndex1,
    updatedAt: updatedAt1,
    createdAt: createdAt1,
    ...view1Base
  } = view1;
  const {
    default: default2,
    orderIndex: orderIndex2,
    updatedAt: updatedAt2,
    createdAt: createdAt2,
    ...view2Base
  } = view2;
  // Resolve both views' columns the same way so an explicit config that matches the default
  // compares equal to an empty/null one — otherwise reverting a Display change leaves
  // "Save for everyone"/"Reset" stuck. (bug #2522)
  const view1Mapped = {
    ...view1Base,
    columns: canonicalColumns(framework, view1),
  };
  const view2Mapped = {
    ...view2Base,
    columns: canonicalColumns(framework, view2),
  };

  let cleanedView1 = cleanObject(view1Mapped);
  let cleanedView2 = cleanObject(view2Mapped);
  if (!cleanedView1.sortBy?.column || cleanedView1.sortBy?.column === 'Manual') {
    cleanedView1.sortBy = { sortBy: 'Manual' };
  }
  if (!cleanedView2.sortBy?.column || cleanedView2.sortBy?.column === 'Manual') {
    cleanedView2.sortBy = { sortBy: 'Manual' };
  }

  return isEqual(cleanedView1, cleanedView2);
}

function cleanObject(obj: any): any {
  if (obj === null || obj === undefined) return undefined;

  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map(cleanObject) // Recursively clean array items
      .filter((item) => item !== undefined); // Remove null/undefined items from arrays

    return cleanedArray.length === 0 ? undefined : cleanedArray;
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleanedObj = Object.entries(obj as Record<string, any>).reduce(
      (acc: { [key: string]: any }, [key, value]) => {
        const cleanedValue = cleanObject(value);
        if (cleanedValue !== undefined) {
          acc[key] = cleanedValue;
        }
        return acc;
      },
      {}
    );

    // Return undefined if the object is empty after cleaning
    return Object.keys(cleanedObj as Record<string, any>).length === 0 ? undefined : cleanedObj;
  }

  return obj;
}

// Canonical { hidden, order } via the same merge/sort the table uses, so empty and default-equal
// configs resolve identically.
const canonicalColumns = (framework: WorkspaceFramework, view: WorkspaceView): WorkspaceViewColumn => {
  const resolved = ViewColumnUtils.updateAndSortColumn(framework, view);
  return {
    hidden: resolved.filter((column) => !column.selected).map((column) => column.id),
    order: resolved.map((column) => column.id),
  };
};

// Writes a new order into the saved views. Only orderIndex changes: the local views a reorder
// starts from can carry unsaved display edits, and copying those in would make them the saved
// baseline, so "Save for Everyone" would vanish for a change that was never saved. (DEV-3219)
export function applyViewsOrder(views: WorkspaceView[], viewsOrder: Record<number, number>): WorkspaceView[] {
  return views.map((view) => (view.id in viewsOrder ? { ...view, orderIndex: viewsOrder[view.id] } : view));
}

// Re-syncs the local views from a fresh server list without dropping unsaved edits. A local
// view that differs from the server copy it was last synced from holds an unsaved change, so it
// keeps its config and takes only orderIndex and default from the server. Every other view
// adopts the server copy. (DEV-3219)
export function mergeServerViews(
  framework: WorkspaceFramework,
  serverViews: WorkspaceView[],
  previousServerViews: WorkspaceView[],
  localViews: WorkspaceView[]
): WorkspaceView[] {
  return serverViews.map((serverView) => {
    const localView = localViews.find((view) => view.id === serverView.id);
    const previousServerView = previousServerViews.find((view) => view.id === serverView.id);
    if (!localView || !previousServerView || areViewsEqual(framework, localView, previousServerView)) {
      return serverView;
    }
    return { ...localView, orderIndex: serverView.orderIndex, default: serverView.default };
  });
}
